import { useCallback, useEffect, useState } from 'react'
import {
  createFeeSchedule,
  getActiveFeeSchedule,
  getFeeScheduleHistory,
  type FeeScheduleCreatePayload,
  type FeeScheduleDetail,
} from '../../services/adminApi'
import { CustomButton } from '../../components/common/CustomButton'
import { CustomTextInput } from '../../components/common/CustomTextInput'
import { usePermission } from '../../hooks/usePermission'

interface FeeSchedulesPanelProps {
  shopId: string
}

const bpsToPct = (bps: number | null): string =>
  bps === null ? '—' : `${(bps / 100).toFixed(2)}%`

const DEFAULT_LABEL = '(default)'

const pctInput = (bps: number | null) => (bps === null ? '' : String(bps / 100))

function ScheduleCard({
  schedule,
  activeLabel,
}: {
  schedule: FeeScheduleDetail
  activeLabel?: string
}) {
  const managed = schedule.management_commission_bps !== null
  return (
    <div className="rounded-xl border border-gray-200 dark:border-[#3d2c4f] bg-white dark:bg-[#2a1f35] p-4">
      {activeLabel && (
        <div className="mb-2 inline-block rounded-full bg-green-50 dark:bg-green-900/30 px-2 py-0.5 text-xs font-medium text-green-700 dark:text-green-300">
          {activeLabel}
        </div>
      )}
      <div className="grid grid-cols-2 gap-3 text-sm">
        <div>
          <div className="text-gray-500 dark:text-gray-400">Seller processing</div>
          <div className="font-medium">
            {schedule.seller_processing_rate_bps === null
              ? DEFAULT_LABEL
              : bpsToPct(schedule.seller_processing_rate_bps)}
          </div>
        </div>
        <div>
          <div className="text-gray-500 dark:text-gray-400">Management commission</div>
          <div className={`font-medium ${managed ? 'text-amber-700 dark:text-amber-300' : ''}`}>
            {managed ? bpsToPct(schedule.management_commission_bps) : 'None'}
          </div>
        </div>
        <div className="col-span-2">
          <div className="text-gray-500 dark:text-gray-400">4% marketplace fee on pickup</div>
          <div
            className={`font-medium ${schedule.waive_pickup_marketplace_rate ? 'text-green-700 dark:text-green-300' : ''}`}
          >
            {schedule.waive_pickup_marketplace_rate ? 'Waived' : 'Charged'}
          </div>
        </div>
        {schedule.contract_reference && (
          <div className="col-span-2">
            <div className="text-gray-500 dark:text-gray-400">Contract</div>
            <div className="font-mono text-xs">{schedule.contract_reference}</div>
          </div>
        )}
        <div className="col-span-2 border-t border-gray-100 dark:border-[#3d2c4f] pt-2 text-xs text-gray-500 dark:text-gray-400">
          Effective {new Date(schedule.effective_from).toLocaleString()}
          {schedule.effective_until &&
            ` → ${new Date(schedule.effective_until).toLocaleString()}`}
        </div>
        {schedule.notes && (
          <div className="col-span-2 text-xs text-gray-600 dark:text-gray-300 italic">
            {schedule.notes}
          </div>
        )}
      </div>
    </div>
  )
}

export function FeeSchedulesPanel({ shopId }: FeeSchedulesPanelProps) {
  const [active, setActive] = useState<FeeScheduleDetail | null>(null)
  const [history, setHistory] = useState<FeeScheduleDetail[]>([])
  const [loading, setLoading] = useState(true)
  const [showForm, setShowForm] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [formError, setFormError] = useState<string | null>(null)

  const [sellerProcessingPct, setSellerProcessingPct] = useState('')
  const [managementPct, setManagementPct] = useState('')
  const [contractRef, setContractRef] = useState('')
  const [notes, setNotes] = useState('')
  const [waivePickup, setWaivePickup] = useState(false)

  const canCreate = usePermission('fee_schedules', 'create')

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const [activeRes, historyRes] = await Promise.all([
        getActiveFeeSchedule(shopId),
        getFeeScheduleHistory(shopId),
      ])
      if (activeRes.ok) setActive(activeRes.data ?? null)
      if (historyRes.ok && historyRes.data) setHistory(historyRes.data.items)
    } finally {
      setLoading(false)
    }
  }, [shopId])

  useEffect(() => {
    load()
  }, [load])

  const resetForm = () => {
    setSellerProcessingPct('')
    setManagementPct('')
    setContractRef('')
    setNotes('')
    setWaivePickup(false)
    setFormError(null)
  }

  // A new schedule replaces the active one outright, so start from what is in effect.
  const openForm = () => {
    setSellerProcessingPct(pctInput(active?.seller_processing_rate_bps ?? null))
    setManagementPct(pctInput(active?.management_commission_bps ?? null))
    setContractRef(active?.contract_reference ?? '')
    setNotes('')
    setWaivePickup(active?.waive_pickup_marketplace_rate ?? false)
    setFormError(null)
    setShowForm(true)
  }

  const handleSubmit = async () => {
    setFormError(null)
    const payload: FeeScheduleCreatePayload = { waive_pickup_marketplace_rate: waivePickup }
    if (sellerProcessingPct.trim() !== '') {
      const pct = Number.parseFloat(sellerProcessingPct)
      if (Number.isNaN(pct) || pct < 0 || pct > 100) {
        setFormError('Seller processing must be a percentage between 0 and 100.')
        return
      }
      payload.seller_processing_rate_bps = Math.round(pct * 100)
    }
    if (managementPct.trim() !== '') {
      const pct = Number.parseFloat(managementPct)
      if (Number.isNaN(pct) || pct < 0 || pct > 100) {
        setFormError('Management commission must be a percentage between 0 and 100.')
        return
      }
      payload.management_commission_bps = Math.round(pct * 100)
    }
    if (
      payload.seller_processing_rate_bps === undefined &&
      payload.management_commission_bps === undefined &&
      !waivePickup &&
      !active?.waive_pickup_marketplace_rate
    ) {
      setFormError('Set seller processing, management commission, or the pickup fee waiver.')
      return
    }
    if (payload.management_commission_bps !== undefined && !contractRef.trim()) {
      setFormError('Contract reference is required when setting management commission.')
      return
    }
    if (contractRef.trim()) payload.contract_reference = contractRef.trim()
    if (notes.trim()) payload.notes = notes.trim()

    setSubmitting(true)
    const res = await createFeeSchedule(shopId, payload)
    setSubmitting(false)
    if (!res.ok) {
      setFormError(res.error?.message || 'Failed to create fee schedule.')
      return
    }
    resetForm()
    setShowForm(false)
    await load()
  }

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <div className="h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent" />
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h4 className="font-semibold text-gray-900 dark:text-gray-100">Active fee schedule</h4>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            Buyer-facing rates use the published platform defaults, except the
            optional 4% waiver on pickup orders.
          </p>
        </div>
        {canCreate && !showForm && (
          <CustomButton onClick={openForm} variant="primary">
            Set new rate
          </CustomButton>
        )}
      </div>

      {active ? (
        <ScheduleCard schedule={active} activeLabel="Active" />
      ) : (
        <div className="rounded-xl bg-gray-50 dark:bg-[#2a1f35] p-4 text-sm text-gray-600 dark:text-gray-300">
          No override in effect. Shop uses published platform defaults.
        </div>
      )}

      {showForm && (
        <div className="rounded-xl border border-gray-200 dark:border-[#3d2c4f] bg-gray-50 dark:bg-[#2a1f35] p-4">
          <h5 className="mb-3 font-medium text-gray-900 dark:text-gray-100">New fee schedule</h5>
          <p className="mb-4 text-xs text-gray-600 dark:text-gray-300">
            Starts from the schedule in effect. Leave a rate blank to use the default.
            Setting management commission requires a contract reference.
          </p>
          <div className="grid gap-3 md:grid-cols-2">
            <CustomTextInput
              label="Seller processing rate (%)"
              value={sellerProcessingPct}
              onChange={(e) => setSellerProcessingPct(e.target.value)}
              placeholder="e.g., 3.1 (leave blank for default)"
            />
            <CustomTextInput
              label="Management commission (%)"
              value={managementPct}
              onChange={(e) => setManagementPct(e.target.value)}
              placeholder="e.g., 25 (blank = not managed)"
            />
            <label className="md:col-span-2 flex items-start gap-2 text-sm text-gray-700 dark:text-gray-300">
              <input
                type="checkbox"
                className="mt-1"
                checked={waivePickup}
                onChange={(e) => setWaivePickup(e.target.checked)}
              />
              <span>
                Waive the 4% marketplace fee on pickup orders
                <span className="block text-xs text-gray-500 dark:text-gray-400">
                  Buyers who pick up in store pay the item price plus the $0.48
                  infrastructure fee. Shipped items still pay 4%.
                </span>
              </span>
            </label>
            <div className="md:col-span-2">
              <CustomTextInput
                label="Contract reference"
                value={contractRef}
                onChange={(e) => setContractRef(e.target.value)}
                placeholder="e.g., MSA-2026-04-16-echoes"
              />
            </div>
            <div className="md:col-span-2">
              <CustomTextInput
                label="Notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Optional context"
              />
            </div>
          </div>
          {formError && (
            <div className="mt-3 rounded-lg bg-red-50 dark:bg-red-900/30 p-3 text-sm text-red-700 dark:text-red-300">
              {formError}
            </div>
          )}
          <div className="mt-4 flex gap-2">
            <CustomButton
              onClick={handleSubmit}
              disabled={submitting}
              variant="primary"
            >
              {submitting ? 'Saving...' : 'Create schedule'}
            </CustomButton>
            <CustomButton
              onClick={() => {
                resetForm()
                setShowForm(false)
              }}
              variant="secondary"
            >
              Cancel
            </CustomButton>
          </div>
        </div>
      )}

      {history.length > 1 && (
        <div>
          <h4 className="mb-3 font-semibold text-gray-900 dark:text-gray-100">History</h4>
          <div className="space-y-2">
            {history
              .filter((s) => s.id !== active?.id)
              .map((s) => (
                <ScheduleCard key={s.id} schedule={s} />
              ))}
          </div>
        </div>
      )}
    </div>
  )
}
